import { useState } from 'react';
import type { Pilot } from '@edh/shared';
import {
  PLACEMENT_GAMES,
  display,
  formatRecord,
  games,
  isRanked,
  matchesForPilot,
  pilotEloSeries,
  pilotRecord,
  rankFor,
  rankThrough,
  deckLabel,
  finishRecord,
  bestDeckFor,
  winRate,
} from '@edh/shared';
import { DeckThumb } from './DeckThumb';
import { RankedAvatar } from './RankedAvatar';
import { EditProfile } from './EditProfile';
import { EloChart } from './EloChart';
import { RankBadge } from './RankBadge';
import { Sheet } from './Sheet';
import { ColorPips } from '../screens/Decks';
import { endSession } from '../lib/cognito';
import { navigate } from '../lib/router';
import { useStore } from '../lib/store';

/**
 * Everything the guild knows about one member, and everything a leader is
 * allowed to do about it.
 *
 * The admin actions live here rather than on the roster row: a list of people
 * with a Remove button beside each name invites a mis-tap, and burying them one
 * layer down costs a tap nobody makes twice a season.
 */
export function MemberProfile({ pilot, onClose }: { pilot: Pilot; onClose: () => void }) {
  const { state, me, isLeader, queued, updatePilot, deletePilot, setLeader, sync } = useStore();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const isMe = me?.id === pilot.id;
  const record = pilotRecord(state.matches, pilot.id);
  const played = matchesForPilot(state.matches, pilot.id);
  const built = state.decks
    .filter((d) => d.ownerId === pilot.id && !d.retired)
    .sort((a, b) => b.elo - a.elo);
  const ranked = isRanked(pilot.placementGames);
  const rate = winRate(record);
  const finish = finishRecord(state.matches, pilot.id);
  const best = bestDeckFor(state.matches, pilot.id);
  const bestDeck = best ? state.decks.find((d) => d.id === best.deckId) : undefined;
  const leaderCount = state.pilots.filter((p) => p.leader).length;
  const untouched = pilot.placementGames === 0 && games(record) === 0;

  // The rank the path arrives at, not the one the number alone would give —
  // the line is drawn in the colour of the halo they are actually wearing.
  const series = pilotEloSeries(state.matches, pilot.id, pilot.elo);
  const title = rankFor(pilot.elo, rankThrough(series).index);

  async function act(fn: () => Promise<unknown>, closeAfter = false) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (closeAfter) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'that did not work');
    } finally {
      setBusy(false);
    }
  }

  if (editing && me && isMe) {
    return <EditProfile me={me} onClose={() => setEditing(false)} onSaved={() => void sync()} />;
  }

  return (
    <Sheet onClose={onClose} title={pilot.name}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
        <RankedAvatar pilot={pilot} size={60} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>{pilot.name}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            {pilot.leader && <span className="pip gold">leader</span>}
            {!pilot.sub && !pilot.retired && <span className="pip">guest</span>}
            {pilot.retired && <span className="pip">hidden</span>}
            {isMe && <span className="pip">you</span>}
          </div>
          {pilot.email && (
            <div
              className="hint"
              style={{
                marginTop: 5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pilot.email}
            </div>
          )}
        </div>
      </div>

      <div className="stat-grid">
        {/* Until it is real, this tile is not a rating and should not claim to
            be one. It shows progress toward becoming one instead. */}
        <div className="stat">
          <div className="k">{ranked ? 'Rating' : 'Placement'}</div>
          <div className="v">
            {ranked ? display(pilot.elo) : `${pilot.placementGames}/${PLACEMENT_GAMES}`}
          </div>
          {ranked && (
            <div style={{ marginTop: 4 }}>
              <RankBadge pilot={pilot} size="small" />
            </div>
          )}
        </div>
        <div className="stat">
          <div className="k">Record</div>
          <div className="v">{formatRecord(record)}</div>
        </div>
        {/* The share of opponents they finish above, which is what a win rate
            should have been all along: only one seat wins a four-player pod, so
            an average win rate is 25% and a good season reads as a bad one.
            Same tile as the standings card, so the two cannot disagree. */}
        <div className="stat">
          <div className="k">{finish ? 'Beaten' : 'Win rate'}</div>
          <div className="v">
            {finish
              ? `${Math.round(finish.score * 100)}%`
              : rate === null
                ? '—'
                : `${Math.round(rate * 100)}%`}
          </div>
          {finish && (
            <div className="hint" style={{ marginTop: 2 }}>
              of opponents
            </div>
          )}
        </div>
        <div className="stat">
          <div className="k">Built</div>
          <div className="v">{built.length}</div>
        </div>
      </div>

      {/* The ladder behind the line, so a climb reads as a climb toward
          something rather than as a number going up.

          Nothing at all until placement is over. The chart is the most complete
          version of the number there is — every value it ever held, and what
          each game was worth — so it is the last thing that should be readable
          while the rating is meant to be sealed. */}
      {ranked && series.length > 1 && (
        <>
          <div className="section-head" style={{ marginTop: 18 }}>
            <h2>Rating</h2>
            <span className="hint">
              {series.length - 1} game{series.length === 2 ? '' : 's'}
            </span>
          </div>
          <EloChart
            series={series}
            color={title.color}
            bands
            label={`${pilot.name}'s rating over every game they have played`}
          />
        </>
      )}

      {/*
        The deck that suits them, which is not the same question as either
        rating on it.

        A deck's own rating says whether the deck is strong and a guildmate's
        says whether they are good; neither says whether this deck works in
        these hands, and that is the one a table actually argues about. Ranked
        on the share of opponents beaten rather than on wins, for the same
        reason the tile above is: only one seat wins a pod.

        Hidden entirely until something has been played enough to say. The
        count sits beside the claim so it can be discounted — three games is a
        small number and the screen does not pretend otherwise.
      */}
      {best && bestDeck && (
        <>
          <div className="section-head" style={{ marginTop: 18 }}>
            <h2>Best with</h2>
            <span className="hint num">
              {best.games} game{best.games === 1 ? '' : 's'}
            </span>
          </div>
          <button
            className="list-item"
            style={{ border: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
            onClick={() => {
              onClose();
              navigate({ name: 'deck', id: bestDeck.id });
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
              <DeckThumb deck={bestDeck} size={38} />
              <span style={{ minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {deckLabel(bestDeck)}
                </span>
                <span className="sub">
                  {best.finish
                    ? `beat ${Math.round(best.finish.score * 100)}% of opponents`
                    : `${best.wins} of ${best.games} won`}
                </span>
              </span>
            </span>
            <span className="row-more" aria-hidden="true">
              ›
            </span>
          </button>
        </>
      )}

      {/* Ownership is a label — anyone can play anything — but it is the only
          thing in the app that records who has been building. */}
      <div className="section-head" style={{ marginTop: 18 }}>
        <h2>Decks built</h2>
      </div>
      {built.length === 0 ? (
        <p className="hint" style={{ margin: '0 2px' }}>
          None registered under this name yet.
        </p>
      ) : (
        <div className="list">
          {built.map((deck) => (
            <button
              key={deck.id}
              className="list-item"
              style={{ border: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
              onClick={() => {
                onClose();
                navigate({ name: 'deck', id: deck.id });
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                <ColorPips colors={deck.colors} />
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {deckLabel(deck)}
                </span>
              </span>
              <span className="num" style={{ fontSize: 14 }}>
                {isRanked(deck.placementGames) ? display(deck.elo) : '—'}
              </span>
            </button>
          ))}
        </div>
      )}

      {played.length > 0 && (
        <p className="hint" style={{ margin: '12px 2px 0' }}>
          Last played {new Date(played[0]!.ts).toLocaleDateString()}.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      {isMe && (
        <>
          <button
            className="btn primary block"
            style={{ marginTop: 18 }}
            onClick={() => setEditing(true)}
          >
            Edit profile
          </button>

          {/*
            Signing out from your own profile, which is where somebody looks
            for it — the Guild tab has it too, but that is the page about
            everybody and this is the page about you.

            Two taps, and the first one warns if anything is still unsent: the
            queue lives on this device, so signing out with changes in it hands
            the phone to an account that cannot flush them.
          */}
          {queued > 0 && (
            <p className="hint" style={{ marginTop: 10 }}>
              {queued} change{queued === 1 ? '' : 's'} still waiting to send. Stay online a
              moment before signing out.
            </p>
          )}
          {signingOut ? (
            <div className="btn-row" style={{ marginTop: 10 }}>
              <button className="btn ghost" onClick={() => setSigningOut(false)}>
                Stay
              </button>
              <button className="btn danger" onClick={endSession}>
                Sign out
              </button>
            </div>
          ) : (
            <button
              className="btn ghost block"
              style={{ marginTop: 10 }}
              onClick={() => setSigningOut(true)}
            >
              Sign out
            </button>
          )}
        </>
      )}

      {isLeader && !isMe && (
        <>
          <div className="section-head" style={{ marginTop: 20 }}>
            <h2>Leader actions</h2>
          </div>
          <div className="btn-row">
            {/* The last leader cannot step down: the guild would be left with
                nobody able to grant the role back. The server agrees. */}
            <button
              className="btn ghost"
              disabled={busy || (pilot.leader && leaderCount <= 1)}
              onClick={() => void act(() => setLeader(pilot.id, !pilot.leader))}
            >
              {pilot.leader ? 'Demote' : 'Make leader'}
            </button>

            {untouched ? (
              confirmRemove ? (
                <button
                  className="btn danger"
                  disabled={busy}
                  onClick={() => void act(() => deletePilot(pilot.id), true)}
                >
                  Confirm remove
                </button>
              ) : (
                <button className="btn danger" disabled={busy} onClick={() => setConfirmRemove(true)}>
                  Remove
                </button>
              )
            ) : (
              <button
                className="btn ghost"
                disabled={busy}
                onClick={() => void act(() => updatePilot(pilot.id, { retired: !pilot.retired }))}
              >
                {pilot.retired ? 'Restore' : 'Hide'}
              </button>
            )}
          </div>
          {!untouched && (
            <p className="hint" style={{ marginTop: 8 }}>
              Members with games on record are hidden rather than removed — their matches
              still refer to them.
            </p>
          )}
        </>
      )}
    </Sheet>
  );
}
